import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  slidesPackagesApi,
  SlidesPackage,
  CreateSlidesPackageDto,
} from '../../../api/slidesBot';
import { UseSlidePackagesReturn } from '../types';

const initialFormData: CreateSlidesPackageDto = {
  code: '',
  name_uz: '',
  name_ru: '',
  decks_included: 5,
  price_uzs: 0,
  is_active: true,
  display_order: 0,
};

export function useSlidePackages(): UseSlidePackagesReturn {
  const [packages, setPackages] = useState<SlidesPackage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<SlidesPackage | null>(null);
  const [formData, setFormData] = useState<CreateSlidesPackageDto>(initialFormData);

  const fetchPackages = useCallback(async () => {
    setIsLoading(true);
    try {
      // Admin always sees inactive packages too — they're filtered out by
      // the bot but still relevant for history/reactivation.
      const data = await slidesPackagesApi.getAll(true);
      setPackages(data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch packages');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const handleCreate = async () => {
    try {
      await slidesPackagesApi.create(formData);
      toast.success('Package created');
      setIsCreateDialogOpen(false);
      setFormData(initialFormData);
      fetchPackages();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create package');
    }
  };

  const handleEdit = (pkg: SlidesPackage) => {
    setEditingPackage(pkg);
    setFormData({
      code: pkg.code,
      name_uz: pkg.name_uz,
      name_ru: pkg.name_ru,
      decks_included: pkg.decks_included,
      price_uzs: pkg.price_uzs,
      is_active: pkg.is_active,
      display_order: pkg.display_order,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingPackage) return;
    try {
      await slidesPackagesApi.update(editingPackage.id, formData);
      toast.success('Package updated');
      setIsEditDialogOpen(false);
      setEditingPackage(null);
      fetchPackages();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update package');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this package? Cannot undo.')) return;
    try {
      await slidesPackagesApi.delete(id);
      toast.success('Package deleted');
      fetchPackages();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete package');
    }
  };

  const handleToggleActive = async (pkg: SlidesPackage) => {
    const next = !pkg.is_active;
    setPackages((prev) =>
      prev.map((p) => (p.id === pkg.id ? { ...p, is_active: next } : p)),
    );
    try {
      await slidesPackagesApi.update(pkg.id, { is_active: next });
      toast.success(next ? 'Package activated' : 'Package deactivated');
    } catch (error: any) {
      setPackages((prev) =>
        prev.map((p) =>
          p.id === pkg.id ? { ...p, is_active: pkg.is_active } : p,
        ),
      );
      toast.error(error.response?.data?.message || 'Failed to toggle status');
    }
  };

  return {
    packages,
    isLoading,
    isCreateDialogOpen,
    isEditDialogOpen,
    editingPackage,
    formData,
    setIsCreateDialogOpen,
    setIsEditDialogOpen,
    setFormData,
    handleCreate,
    handleEdit,
    handleUpdate,
    handleDelete,
    handleToggleActive,
  };
}
